import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import CatalogueView from './view';
import { getVenue, updateManagementWorkArea, addManagementWorkArea, bulkActivateWorkAreaResources } from '../../../api';

const Catalogue = ({ venueData, onVenueUpdate }) => {
  // Simplified state - only what we actually need
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [venueInfo, setVenueInfo] = useState(null);

  // Use ref to track venue slug to prevent unnecessary re-fetches
  const venueSlugRef = useRef(venueData?.slug);
  const hasInitialFetch = useRef(false); // Track if we've done initial fetch

  // SIMPLIFIED: Just fetch and store venue data
  const fetchVenueData = useCallback(async () => {
    const currentSlug = venueSlugRef.current;

    if (currentSlug) {
      setIsLoading(true);
      try {
        const response = await getVenue(currentSlug);

        if (response) {
          setVenueInfo(response);
          hasInitialFetch.current = true;
        }
      } catch (error) {
        console.error('Error fetching venue data:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, []); // No dependencies - this function logic doesn't change

  // Initial load and when venue changes
  useEffect(() => {
    const newSlug = venueData?.slug;

    // Only fetch if:
    // 1. We have a slug AND
    // 2. Either we haven't done initial fetch OR the slug actually changed
    if (newSlug && (!hasInitialFetch.current || newSlug !== venueSlugRef.current)) {
      venueSlugRef.current = newSlug;
      fetchVenueData();
    }
  }, [venueData?.slug]); // Only depend on the slug, not the function

  // MEMOIZED: Function to refresh venue data (for sibling components)
  const refreshVenueData = useCallback(async () => {
    await fetchVenueData();
    // Also notify parent to refresh venue data (e.g., for the activation checklist)
    if (onVenueUpdate) {
      onVenueUpdate();
    }
  }, [fetchVenueData, onVenueUpdate]);

  // Make refreshVenueData available globally (keeping this for backward compatibility)
  useEffect(() => {
    window.refreshCatalogueData = refreshVenueData;
    return () => {
      delete window.refreshCatalogueData;
    };
  }, [refreshVenueData]);

  const meetingRooms = useMemo(() => {
    if (!venueInfo?.floors) return [];
    let allMeetingRooms = [];
    venueInfo.floors.forEach(floor => {
      if (floor.work_areas) {
        floor.work_areas.forEach(workArea => {
          if (workArea.meeting_rooms) {
            allMeetingRooms = [...allMeetingRooms, ...workArea.meeting_rooms.map(room => ({
              ...room,
              work_area: workArea.slug, // Add work area reference
              floor: floor // Add floor reference
            }))];
          }
        });
      }
    });
    return allMeetingRooms;
  }, [venueInfo]);

  // DERIVED DATA: Calculate from venueInfo instead of storing separately
  const workAreas = useMemo(() => {
    if (!venueInfo?.floors) return [];

    let allWorkAreas = [];
    venueInfo.floors.forEach(floor => {
      if (floor.work_areas) {
        allWorkAreas = [...allWorkAreas, ...floor.work_areas.map(wa => ({
          ...wa,
          floor: floor // Add floor reference
        }))];
      }
    });
    return allWorkAreas;
  }, [venueInfo]);

  const resources = useMemo(() => {
    if (!venueInfo?.floors) return [];

    let allResources = [];
    venueInfo.floors.forEach(floor => {
      if (floor.work_areas) {
        floor.work_areas.forEach(workArea => {
          // Extract hot desks
          if (workArea.hot_desks) {
            allResources = [...allResources, ...workArea.hot_desks.map(desk => ({
              ...desk,
              resource_type: 'HD',
              work_area: workArea.slug
            }))];
          }

          // Extract dedicated desks
          if (workArea.dedicated_desks) {
            allResources = [...allResources, ...workArea.dedicated_desks.map(desk => ({
              ...desk,
              resource_type: 'DE',
              work_area: workArea.slug
            }))];
          }

          // Extract meeting rooms
          if (workArea.meeting_rooms) {
            allResources = [...allResources, ...workArea.meeting_rooms.map(room => ({
              ...room,
              resource_type: 'MR',
              work_area: workArea.slug
            }))];
          }
        });
      }
    });
    return allResources;
  }, [venueInfo]);

  const services = useMemo(() => {
    if (!venueInfo) return [];

    const allServices = new Set();

    // Extract services from work areas
    if (venueInfo.floors) {
      venueInfo.floors.forEach(floor => {
        if (floor.work_areas) {
          floor.work_areas.forEach(workArea => {
            if (workArea.services) {
              workArea.services.forEach(service => {
                allServices.add(service);
              });
            }
          });
        }
      });
    }

    // Also collect venue-level services
    if (venueInfo.venue_services) {
      venueInfo.venue_services.forEach(service => {
        allServices.add(service);
      });
    }

    return Array.from(allServices);
  }, [venueInfo]);

  // MEMOIZED: Data manipulation and filtering functions
  const getResourcesByType = useCallback((type) => {
    return resources.filter(resource => resource.resource_type === type);
  }, [resources]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // MEMOIZED: API-based function to handle work area creation
  const handleAddWorkArea = useCallback(async (requestData) => {
    try {
      let response;

      if (typeof addManagementWorkArea === 'function') {
        response = await addManagementWorkArea(requestData, venueSlugRef.current);
      } else {
        response = await updateManagementWorkArea(requestData, venueSlugRef.current);
      }

      if (response && response.success) {
        // Refresh the venue data to get the latest state
        await refreshVenueData();

        return {
          success: true,
          work_area: response.work_area,
          message: 'Work area created successfully'
        };
      } else {
        throw new Error(response?.error || 'Failed to create work area');
      }
    } catch (error) {
      console.error('Error creating work area:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to create work area'
      };
    }
  }, [refreshVenueData]);

  // MEMOIZED: Function to handle work area management API calls (for updates)
  const handleUpdateWorkAreaApi = useCallback(async (requestData) => {
    try {
      const response = await updateManagementWorkArea(requestData, venueSlugRef.current);

      if (response && response.success) {
        // Just refresh the data instead of manually updating state
        await refreshVenueData();
        return response;
      } else {
        throw new Error(response?.error || 'Failed to update work area');
      }
    } catch (error) {
      console.error('Error updating work area:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }, [refreshVenueData]);

  // Simplified handlers - most of these might not be needed if you're only reading data
  const handleDeleteWorkArea = useCallback((slug) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleAddResource = useCallback((newResource) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleUpdateResource = useCallback((updatedResource) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleDeleteResource = useCallback((id) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleAddTariff = useCallback((newTariff) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleUpdateTariff = useCallback((updatedTariff) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  const handleDeleteTariff = useCallback((id) => {
    // Implement API call + refresh
    refreshVenueData();
  }, [refreshVenueData]);

  // MEMOIZED: Bulk activate resources handler
  const handleBulkActivateResources = useCallback(async (workAreaSlug, resourceType = null) => {
    try {
      const response = await bulkActivateWorkAreaResources(workAreaSlug, resourceType);
      
      if (response && response.success) {
        // Refresh the venue data to show updated resource statuses
        await refreshVenueData();
        return {
          success: true,
          activatedCount: response.activated_count,
          message: response.message
        };
      } else {
        throw new Error(response?.error || 'Failed to activate resources');
      }
    } catch (error) {
      console.error('Error bulk activating resources:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to activate resources'
      };
    }
  }, [refreshVenueData]);

  // MEMOIZED: Props object to prevent unnecessary re-renders of CatalogueView
  const catalogueViewProps = useMemo(() => ({
    venueSlug: venueSlugRef.current,
    workAreas,
    meetingRooms,
    resources,
    floors: venueInfo?.floors || [],
    services,
    tariffs: [], // You might want to derive this from venueInfo too
    activeTab,
    handleTabChange,
    handleAddWorkArea,
    handleUpdateWorkArea: handleUpdateWorkAreaApi,
    handleDeleteWorkArea,
    handleAddResource,
    handleUpdateResource,
    handleDeleteResource,
    handleAddTariff,
    handleUpdateTariff,
    handleDeleteTariff,
    handleBulkActivateResources,
    getResourcesByType,
    venueInfo,
    isLoading,
  }), [
    workAreas,
    resources,
    meetingRooms,
    venueInfo,
    services,
    activeTab,
    isLoading,
    handleTabChange,
    handleAddWorkArea,
    handleUpdateWorkAreaApi,
    handleDeleteWorkArea,
    handleAddResource,
    handleUpdateResource,
    handleDeleteResource,
    handleAddTariff,
    handleUpdateTariff,
    handleDeleteTariff,
    handleBulkActivateResources,
    getResourcesByType,
  ]);

  return <CatalogueView {...catalogueViewProps} />;
};

export default Catalogue;